import Footer from "./Footer";
import { Outlet } from "react-router";

import { useEffect } from "react";

import Navbar from "./Navbar";
import SubNav from "./SubNav";
import type {
  AuthenticationProps,
  RefreshTimePayload,
} from "@/shared/types/common.types";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useTimezone } from "@/contexts/TimeZoneContext";
import { fetchLastRefreshTime } from "@/features/admin/store/configSlice";
import { selectIsCustomer, selectUsername } from "@/store/authSlice";
import {
  fetchMasterProjects,
  fetchCustomersProjectEnv,
} from "@/features/alerts/store/alertsSlice";
import {
  fetchMasterSecurityAlerts,
  fetchServices,
  fetchSubscriptions,
} from "@/features/security-alerts/store/securityAlertsSlice";
import { fetchMasterProductVersion } from "@/features/deployment/store/deploymentSlice";

function Layout({
  logout,
  jwtReady,
}: AuthenticationProps & { jwtReady: boolean }) {
  const dispatch = useAppDispatch();
  const { timezone } = useTimezone();
  const isCustomer = useAppSelector(selectIsCustomer);
  const username = useAppSelector(selectUsername);
  useEffect(() => {
    if (!jwtReady) {
      console.log("JWT not ready yet, skipping API calls");
      return;
    }

    if (!username) {
      console.warn("User not authenticated, skipping API calls");
      return;
    }

    // Calls needed for all users
    dispatch(fetchCustomersProjectEnv());

    // Projects not needed for Customer — dropdown is hidden and filter is skipped
    if (!isCustomer) {
      dispatch(fetchMasterProjects());
    }

    if (timezone) {
      const refreshPayload: RefreshTimePayload = {
        currentPage: "CriticalAlerts",
        timezone: timezone?.ianaTimeZone,
      };
      dispatch(fetchLastRefreshTime(refreshPayload));
    }

    // Calls not needed for Customer users
    if (!isCustomer) {
      if (timezone) {
        dispatch(fetchMasterSecurityAlerts(timezone?.ianaTimeZone));
      }
      dispatch(fetchMasterProductVersion());
      dispatch(fetchSubscriptions());
      dispatch(fetchServices());
    }
  }, [dispatch, timezone, jwtReady, username, isCustomer]);

  return (
    <div className="flex flex-col h-dvh min-h-full h-screen overflow-x-hidden bg-[var(--bg-canvas)]">
      {/* Header */}
      <header className="flex flex-col w-full sticky top-0 z-999">
        <Navbar logout={logout} />
        <SubNav />
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto w-full relative isolate">
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Layout;
